import React from 'react';
import { Link } from 'react-router-dom';
import {
    Send,
    CheckCircle,
    Gavel,
    Schedule,
    RateReview,
    PlaylistAddCheck,
    GroupAdd
} from '@mui/icons-material';
import { getConfig } from '../../common/env-config';

const CreatorOutreach = () => {
    // --- Visual Components (Mock Graphs/Animations) ---
    const { ecWebsiteUrl } = getConfig();

    // 1. Pipeline Visual (Hero)
    const PipelineVisual = () => (
        <div className="relative w-full aspect-square md:aspect-[4/3] rounded-3xl bg-white/40 backdrop-blur-md border border-white/50 shadow-xl overflow-hidden p-6 flex flex-col">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-blue-500"></div>
            <div className="flex justify-between items-center mb-6">
                <div className="text-xs font-bold uppercase text-gray-500 tracking-wider">Campaign Pipeline</div>
                <div className="px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">Active</div>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-4">
                {/* Stage 1 */}
                <div className="flex items-center gap-3 animate-fade-in-up delay-100">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">1</div>
                    <div className="flex-1 bg-white/60 rounded-lg p-3 shadow-sm border border-white/50 flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Shortlisted</span>
                        <span className="text-xs text-gray-400">45 Creators</span>
                    </div>
                </div>
                {/* Stage 2 */}
                <div className="flex items-center gap-3 animate-fade-in-up delay-200">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">2</div>
                    <div className="flex-1 bg-white/60 rounded-lg p-3 shadow-sm border border-white/50 flex justify-between items-center relative overflow-hidden">
                        <div className="absolute left-0 top-0 h-full w-2/3 bg-blue-50/50"></div>
                        <span className="relative z-10 text-sm font-semibold text-gray-800">Pitched</span>
                        <span className="relative z-10 text-xs text-blue-600 font-bold">28 Contacted</span>
                    </div>
                </div>
                {/* Stage 3 */}
                <div className="flex items-center gap-3 animate-fade-in-up delay-300">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-600">3</div>
                    <div className="flex-1 bg-white/90 rounded-lg p-3 shadow-md border-l-4 border-purple-500 flex justify-between items-center transform scale-105">
                        <span className="text-sm font-bold text-gray-900">Negotiating</span>
                        <span className="text-xs text-purple-600 font-bold">12 Active</span>
                    </div>
                </div>
                {/* Stage 4 */}
                <div className="flex items-center gap-3 animate-fade-in-up delay-400">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600">4</div>
                    <div className="flex-1 bg-white/60 rounded-lg p-3 shadow-sm border border-white/50 flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Confirmed</span>
                        <span className="text-xs text-green-600 font-bold">8 Locked</span>
                    </div>
                </div>
            </div>
        </div>
    );

    // 2. Negotiation Visual
    const NegotiationVisual = () => (
        <div className="relative w-full aspect-[4/3] rounded-3xl bg-gray-50 border border-gray-100 overflow-hidden flex flex-col p-4 shadow-inner">
            <div className="absolute top-0 right-0 p-3 opacity-10"><Gavel fontSize="large" /></div>
            <div className="space-y-3 mt-auto">
                {/* Brand Message */}
                <div className="flex justify-end animate-fade-in-up delay-100">
                    <div className="bg-purple-600 text-white text-xs p-3 rounded-t-2xl rounded-bl-2xl max-w-[80%] shadow-lg">
                        Can we lock this at $500 includes rights?
                    </div>
                </div>
                {/* Creator Reply */}
                <div className="flex justify-start animate-fade-in-up delay-300">
                    <div className="bg-white text-gray-800 text-xs p-3 rounded-t-2xl rounded-br-2xl border border-gray-200 shadow-sm max-w-[80%]">
                        Make it $600 and you have a deal! 🤝
                    </div>
                </div>
                {/* Confirmed State */}
                <div className="flex justify-center py-2 animate-fade-in-up delay-500">
                    <div className="bg-green-100 text-green-700 text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 border border-green-200">
                        <CheckCircle sx={{ fontSize: 12 }} /> Budget Locked: $600
                    </div>
                </div>
            </div>
        </div>
    );

    // 3. List Visual
    const ListVisual = () => (
        <div className="relative w-full aspect-[4/3] rounded-3xl bg-indigo-50 border border-indigo-100 overflow-hidden p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between text-indigo-800 mb-2">
                <span className="text-xs font-bold uppercase">Summer Campaign List</span>
                <PlaylistAddCheck fontSize="small" />
            </div>
            {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white p-2 rounded-xl border border-indigo-100 shadow-sm flex items-center gap-3 animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                    <div className="w-8 h-8 rounded-full bg-gray-200"></div>
                    <div className="flex-1">
                        <div className="h-2 bg-gray-200 rounded w-1/2 mb-1"></div>
                        <div className="h-2 bg-gray-100 rounded w-1/3"></div>
                    </div>
                    <div className="w-4 h-4 rounded-full border-2 border-indigo-200"></div>
                </div>
            ))}
            <div className="mt-auto text-center">
                <div className="inline-block bg-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-lg shadow-md hover:scale-105 transition-transform cursor-pointer">
                    Add 15 Creators
                </div>
            </div>
        </div>
    );

    // 4. Approval Visual
    const ApprovalVisual = () => (
        <div className="relative w-full aspect-[4/3] rounded-3xl bg-pink-50 border border-pink-100 overflow-hidden flex items-center justify-center p-6">
            <div className="bg-white w-full rounded-2xl shadow-xl p-4 border border-white/60 relative">
                <div className="absolute -top-3 -right-3 bg-yellow-400 text-black text-[10px] font-bold px-2 py-1 rounded shadow-md animate-bounce-custom">Review Pending</div>
                <div className="aspect-video bg-gray-100 rounded-lg mb-3 flex items-center justify-center text-gray-400">
                    <span className="text-xs">Video Draft.mp4</span>
                </div>
                <div className="flex gap-2">
                    <div className="flex-1 bg-red-50 text-red-600 text-xs font-bold py-2 rounded-lg text-center cursor-pointer hover:bg-red-100 transition-colors">Request Changes</div>
                    <div className="flex-1 bg-green-500 text-white text-xs font-bold py-2 rounded-lg text-center cursor-pointer hover:bg-green-600 transition-colors shadow-lg shadow-green-200">Approve</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 text-gray-900 font-sans">
            <style>
                {`
                    .glass-nav { backdrop-filter: blur(10px); background: rgba(255,255,255,0.8); border-bottom: 1px solid rgba(255,255,255,0.3); }
                    .glass-card { backdrop-filter: blur(12px); background: rgba(255, 255, 255, 0.7); border: 1px solid rgba(255, 255, 255, 0.5); box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.05); }
                `}
            </style>

            {/* Sub-Nav (Campaign Management) */}
            <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-3 sm:px-6 lg:px-8 shadow-sm">
                <div className="mx-auto max-w-7xl flex flex-wrap items-center gap-4 text-sm">
                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 font-bold text-xs uppercase tracking-wide">Campaign Management</span>
                    <nav className="flex items-center gap-6 overflow-x-auto no-scrollbar">
                        <Link to="#" className="text-gray-500 hover:text-gray-900 font-medium whitespace-nowrap transition-colors">Media Plans</Link>
                        <Link to="/product/creator-outreach" className="text-secondary-600 font-bold whitespace-nowrap border-b-2 border-secondary-600 pb-0.5">Creator Outreach</Link>
                        <Link to="/product/creator-payments" className="text-gray-500 hover:text-gray-900 font-medium whitespace-nowrap transition-colors">Payments</Link>
                        <Link to="#" className="text-gray-500 hover:text-gray-900 font-medium whitespace-nowrap transition-colors">Reporting</Link>
                    </nav>
                </div>
            </div>

            {/* Hero Section */}
            <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="relative z-10">
                            <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight mb-6">
                                Outreach that turns <br className="hidden lg:block" />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Shortlisted Creators</span> into <br className="hidden lg:block" />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Confirmed Deliverables</span>
                            </h1>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
                                Start conversations fast, qualify interest, negotiate budgets, and move creators into shoot + submission workflows—without losing context across Ops, Servicing, and the Creator.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="rounded-full bg-primary-500 px-8 py-3.5 text-base font-bold text-white hover:bg-primary-600 hover:shadow-lg hover:scale-105 transition-all shadow-primary-500/30 text-center inline-block">
                                    Start Outreach
                                </a>
                                <a href="https://outlook.office365.com/book/ProductDemo@ttlmedia.in/?ismsaljsauthenabled=true" target="_blank" rel="noopener noreferrer" className="rounded-full border border-gray-300 bg-white px-8 py-3.5 text-base font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all text-center inline-block">
                                    Request Demo
                                </a>
                            </div>

                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                                <CheckCircle fontSize="inherit" className="text-green-500" />
                                <span>Built for high-volume nano & micro creator campaigns across cities.</span>
                            </div>
                        </div>

                        <div className="relative animate-float">
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
                            <PipelineVisual />
                            <div className="mt-4 text-center text-xs text-gray-400 font-medium">
                                * Actual dashboard visualization includes detailed timestamps and logs.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Intro Section */}
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="rounded-[2.5rem] bg-white border border-gray-100 shadow-xl p-8 lg:p-12 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
                    <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
                        <div className="max-w-2xl">
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">From “shortlist” to “confirmed” in one workflow</h2>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                Oye Creators Outreach is not just messaging. It is a structured pipeline to manage creator conversations, align budgets, lock timelines, and push the creator into campaign execution with task-level clarity.
                            </p>
                        </div>
                        <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 rounded-2xl bg-secondary-600 px-6 py-4 text-sm font-bold text-white hover:bg-secondary-700 hover:shadow-lg transition-all inline-block">
                            Create Outreach List
                        </a>
                    </div>
                </div>
            </section>

            {/* Feature Blocks */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="grid gap-8 lg:gap-12">

                    {/* Feature 1: Outreach Lists */}
                    <div className="group grid items-center gap-8 rounded-[2rem] glass-card p-8 lg:grid-cols-2 lg:p-12 hover:shadow-2xl transition-all duration-500">
                        <div className="order-2 lg:order-1">
                            <ListVisual />
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center text-indigo-600 mb-6 group-hover:scale-110 transition-transform">
                                <GroupAdd />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Build an outreach list in minutes</h3>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                Add creators directly from Discovery or your saved lists. Segment by city, follower range, niche, and campaign fit—so Ops teams can start pitching immediately.
                            </p>
                            <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="text-primary-600 font-bold hover:text-primary-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                Build an Outreach List →
                            </a>
                        </div>
                    </div>

                    {/* Feature 2: Pitch + Negotiate */}
                    <div className="group grid items-center gap-8 rounded-[2rem] glass-card p-8 lg:grid-cols-2 lg:p-12 hover:shadow-2xl transition-all duration-500">
                        <div className="order-1">
                            <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center text-purple-600 mb-6 group-hover:scale-110 transition-transform">
                                <Send />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Pitch and negotiate inside the same thread</h3>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                Track interest, negotiate commercial terms, and lock pricing with conversation context attached to each creator profile. No back-and-forth across WhatsApp screenshots and spreadsheets.
                            </p>
                            <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="text-primary-600 font-bold hover:text-primary-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                Start pitching creators →
                            </a>
                        </div>
                        <div className="order-2">
                            <NegotiationVisual />
                        </div>
                    </div>

                    {/* Feature 3: Confirm + Schedule */}
                    <div className="group grid items-center gap-8 rounded-[2rem] glass-card p-8 lg:grid-cols-2 lg:p-12 hover:shadow-2xl transition-all duration-500">
                        <div className="order-2 lg:order-1">
                            {/* Placeholder generic visual for now, or reuse ListVisual with green theme */}
                            <div className="relative w-full aspect-[4/3] rounded-3xl bg-green-50 border border-green-100 overflow-hidden flex items-center justify-center p-6">
                                <Schedule sx={{ fontSize: 80, color: '#10b981', opacity: 0.2 }} />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="bg-white p-4 rounded-xl shadow-lg border border-green-100 text-center">
                                        <div className="text-sm font-bold text-gray-900">Shoot Locked 🔒</div>
                                        <div className="text-xs text-gray-500 mt-1">Oct 24, 2:00 PM</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center text-green-600 mb-6 group-hover:scale-110 transition-transform">
                                <CheckCircle />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Confirm participation and schedule shoots</h3>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                Once the creator agrees, capture confirmation details: deliverables, shoot dates, location, timelines, and brand instructions. Convert the conversation into tasks and deadlines automatically.
                            </p>
                            <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="text-primary-600 font-bold hover:text-primary-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                Convert to Campaign Tasks →
                            </a>
                        </div>
                    </div>

                    {/* Feature 4: Submission Review */}
                    <div className="group grid items-center gap-8 rounded-[2rem] glass-card p-8 lg:grid-cols-2 lg:p-12 hover:shadow-2xl transition-all duration-500">
                        <div className="order-1">
                            <div className="w-12 h-12 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600 mb-6 group-hover:scale-110 transition-transform">
                                <RateReview />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Manage submissions, revisions, and posting approvals</h3>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                Collect drafts, share change requests, and maintain a clean audit trail. After final approval, schedule posting, collect live links, and handoff reporting inputs to the team.
                            </p>
                            <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="text-primary-600 font-bold hover:text-primary-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                Manage revisions →
                            </a>
                        </div>
                        <div className="order-2">
                            <ApprovalVisual />
                        </div>
                    </div>

                </div>
            </section>

            {/* Steps Strip */}
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="rounded-[2.5rem] bg-slate-900 text-white p-8 lg:p-16 relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

                    <div className="relative z-10 text-center mb-12">
                        <h2 className="text-3xl font-bold text-white mb-2">How Outreach works in Oye Creators</h2>
                        <p className="text-gray-400">A daily workflow your Ops team can run for every campaign.</p>
                    </div>

                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { step: 1, title: "Shortlist creators from Discovery", desc: "Add creators into an Outreach List by city, niche, follower range, or campaign fit." },
                            { step: 2, title: "Pitch the campaign brief", desc: "Send a structured pitch: deliverables, timelines, location requirements, and expected quality." },
                            { step: 3, title: "Qualify interest + collect questions", desc: "Capture interest status and creator queries (script, brand expectations, posting date, budget)." },
                            { step: 4, title: "Negotiate and lock commercials", desc: "Finalize payout range and terms; tag the creator as Confirmed / On Hold / Not Interested." },
                            { step: 5, title: "Schedule shoot + assist with scripts", desc: "Fix shoot date/time, share script concept, support caption drafts and content alignment." },
                            { step: 6, title: "Collect draft + request revisions", desc: "Review drafts, share changes, and move to “Approved for Posting” once finalized." },
                        ].map((item, i) => (
                            <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/20 transition-colors">
                                <div className="text-xs font-bold text-primary-400 uppercase tracking-wider mb-3">Step {item.step}</div>
                                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                                <p className="text-sm text-gray-300 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-4 py-16">
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Frequently asked questions</h2>
                <div className="space-y-4">
                    {[
                        { q: "What is Outreach in Oye Creators?", a: "Outreach is the structured workflow to contact creators, qualify interest, negotiate commercials, confirm participation, and move them into shoot + submission + posting timelines." },
                        { q: "Can Outreach support nano and micro creators at scale?", a: "Yes. Outreach Lists let Ops teams manage high volumes by city and campaign fit, while keeping each creator’s commercial terms and timelines documented." },
                        { q: "How do we track creator status?", a: "Each creator moves through statuses like Shortlisted, Pitched, Interested, Negotiation, Confirmed, Draft Submitted, Changes Requested, Approved, Posted." },
                        { q: "Does Outreach handle scripts and captions?", a: "Yes—Ops can share script concepts and caption guidance as part of the workflow to improve first-time approval rates." },
                        { q: "How does Outreach connect to campaign execution?", a: "Once confirmed, Outreach converts into campaign tasks and deadlines, enabling servicing to manage reviews, approvals, and posting schedules." }
                    ].map((faq, i) => (
                        <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:border-gray-300 transition-colors">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                            <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-[2.5rem] bg-indigo-900 p-10 lg:p-14 text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <h3 className="text-3xl font-bold mb-4">See the Outreach workflow end-to-end</h3>
                        <p className="text-indigo-200 mb-8 max-w-sm">
                            Walkthrough for Outreach Lists, negotiation tracking, scheduling, submissions, and approval handoffs.
                        </p>
                    </div>

                    <div className="rounded-[2.5rem] bg-white border border-gray-100 p-10 lg:p-14 text-gray-900 shadow-xl relative overflow-hidden group">
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-100 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                        <h3 className="text-3xl font-bold mb-4">Start with one campaign pilot</h3>
                        <p className="text-gray-600 mb-8 max-w-sm">
                            Run Outreach for a single campaign first—then scale to multiple lists and multi-city sourcing.
                        </p>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default CreatorOutreach;
